import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Star, MapPin, IndianRupee, MessageSquare, 
  Scissors, Check, Search, LayoutGrid, 
  List, AlertTriangle, ChevronLeft, ChevronRight,
  X
} from "lucide-react";
import { useSalons, useCategories, useToast } from "../api/hooks";
import { useAuth } from "../App";
import Navbar  from "../components/Navbar";
import Footer  from "../components/Footer";
import Bokeh   from "../components/Bokeh";
import { Stars, Toast, BtnGold, Badge, Spinner } from "../components/UI";
import { T, GLOBAL_STYLES } from "../tokens";
import { useTranslation } from "../i18n";

const CITIES = ["All Cities","Chennai","Mumbai","Bangalore","Delhi","Hyderabad","Pune","Kolkata"];
// Sort labels resolve via t() at render time; keep icons inline for flavour.
const SORT_OPTIONS = [
  {v:"rating",     key:"salons.sort_rating",    icon: <Star size={14} />},
  {v:"distance",   key:"salons.sort_nearest",   icon: <MapPin size={14} />},
  {v:"price_asc",  key:"salons.sort_price_asc", icon: <IndianRupee size={14} />},
  {v:"price_desc", key:"salons.sort_price_desc",icon: <IndianRupee size={14} />},
  {v:"reviews",    key:"salons.sort_relevance", icon: <MessageSquare size={14} />},
];

function SalonCard({ salon, view }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [hov, setHov] = useState(false);
  const cover = salon.images?.find(i => i.is_cover)?.url;
  const bg = cover ? `url(${cover}) center/cover` : "linear-gradient(160deg,#3D2B1A,#1A1008)";

  if (view === "list") return (
    <div onClick={() => navigate(`/salons/${salon.slug}`)} onMouseOver={() => setHov(true)} onMouseOut={() => setHov(false)}
      style={{ display:"flex", background:T.dark3, border:`1px solid ${hov?"rgba(201,168,76,.5)":T.goldBdr}`, borderRadius:14, overflow:"hidden", cursor:"pointer", transition:"all .25s", transform:hov?"translateX(4px)":"none" }}>
      <div style={{ width:170, flexShrink:0, background:bg, position:"relative", minHeight:120, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {!cover && <Scissors size={40} color={T.goldLt} style={{ opacity: 0.2 }} />}
        {salon.is_featured && <div style={{ position:"absolute", top:8, left:8 }}><Badge variant="gold">Featured</Badge></div>}
      </div>
      <div style={{ flex:1, padding:"16px 18px" }}>
        <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:19, fontWeight:600, color:T.text }}>{salon.name}</div>
        <div style={{ fontSize:12, color:T.muted, marginBottom:8, display:"flex", alignItems:"center", gap:4 }}><MapPin size={12} color={T.gold} /> {salon.area}, {salon.city}</div>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <Stars rating={salon.rating} size={12}/>
          <span style={{ fontSize:12.5, fontWeight:600, color:T.goldLt }}>{salon.rating}</span>
          <span style={{ fontSize:11.5, color:T.muted }}>({salon.review_count?.toLocaleString("en-IN")})</span>
        </div>
      </div>
      <div style={{ padding:"16px 18px", display:"flex", flexDirection:"column", alignItems:"flex-end", justifyContent:"space-between", borderLeft:`1px solid ${T.goldBdr}`, flexShrink:0 }}>
        <div style={{ textAlign:"right" }}>
          <div style={{ fontSize:11, color:T.muted }}>{t("salons.from")}</div>
          <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:19, fontWeight:600, color:T.goldLt }}>{salon.min_price ? `₹${Number(salon.min_price).toLocaleString("en-IN")}` : "—"}</div>
        </div>
        <BtnGold size="sm" onClick={e => { e.stopPropagation(); navigate(`/salons/${salon.slug}`); }}>{t("salons.book_now")}</BtnGold>
      </div>
    </div>
  );

  return (
    <div onClick={() => navigate(`/salons/${salon.slug}`)} onMouseOver={() => setHov(true)} onMouseOut={() => setHov(false)}
      style={{ background:T.dark3, border:`1px solid ${hov?"rgba(201,168,76,.5)":T.goldBdr}`, borderRadius:16, overflow:"hidden", cursor:"pointer", transition:"all .28s", transform:hov?"translateY(-5px)":"none", boxShadow:hov?`0 14px 40px rgba(0,0,0,.45)`:""  }}>
      <div style={{ height:168, background:bg, position:"relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {!cover && <Scissors size={50} color={T.goldLt} style={{ opacity: 0.2 }} />}
        <div style={{ position:"absolute", inset:0, background:"linear-gradient(0deg,rgba(13,11,8,.8) 0%,transparent 55%)" }}/>
        <div style={{ position:"absolute", top:10, right:10, background:"rgba(13,11,8,.8)", backdropFilter:"blur(6px)", border:`1px solid ${T.goldBdrS}`, padding:"4px 10px", borderRadius:20, display:"flex", alignItems:"center", gap:6 }}>
          <Star size={10} color={T.gold} fill={T.gold} />
          <span style={{ fontSize:12, fontWeight:600, color:T.goldLt }}>{salon.rating}</span>
        </div>
        {salon.is_featured && <div style={{ position:"absolute", top:10, left:10 }}><Badge variant="gold">Featured</Badge></div>}
      </div>
      <div style={{ padding:"14px 16px" }}>
        <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:17, fontWeight:600, color:T.text, marginBottom:3 }}>{salon.name}</div>
        <div style={{ fontSize:12, color:T.muted, marginBottom:7, display:"flex", alignItems:"center", gap:4 }}><MapPin size={12} color={T.gold} /> {salon.area}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Stars rating={salon.rating} size={11}/>
          <span style={{ fontSize:11, color:T.muted }}>({salon.review_count?.toLocaleString("en-IN")})</span>
        </div>
      </div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 16px 14px", borderTop:`1px solid ${T.goldBdr}` }}>
        <span style={{ fontSize:12, color:T.muted }}>From <span style={{ color:T.goldLt, fontWeight:500 }}>{salon.min_price ? `₹${Number(salon.min_price).toLocaleString("en-IN")}` : "—"}</span></span>
        <BtnGold size="sm" onClick={e => { e.stopPropagation(); navigate(`/salons/${salon.slug}`); }}>{t("salons.book_now")}</BtnGold>
      </div>
    </div>
  );
}

// Known category slugs get localised labels; anything else from the
// backend falls back to the server-supplied name (English today).
const CATEGORY_LABEL_KEY = {
  all:      "salons.categories_all",
  bridal:   "salons.cat_bridal",
  hair:     "academy.hair",
  skin:     "academy.skin",
  nails:    "academy.nails",
  spa:      "salons.cat_spa",
  grooming: "academy.grooming",
  makeup:   "academy.makeup",
};

function FilterPanel({ filters, onChange, onReset, categories }) {
  const { t } = useTranslation();
  const catLabel = (c) => {
    const slug = c.slug || c.id;
    const key = CATEGORY_LABEL_KEY[slug];
    return key ? t(key) : c.name;
  };
  return (
    <div style={{ background:T.dark3, border:`1px solid ${T.goldBdr}`, borderRadius:16, padding:20, display:"flex", flexDirection:"column", gap:20 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <span style={{ fontFamily:"Cormorant Garamond,serif", fontSize:18, fontWeight:600, color:T.text }}>{t("salons.filters_title")}</span>
        <span onClick={onReset} style={{ fontSize:12, color:T.muted, cursor:"pointer", transition: "color .2s" }} onMouseOver={e=>e.currentTarget.style.color=T.goldLt} onMouseOut={e=>e.currentTarget.style.color=T.muted}>{t("salons.reset_all")}</span>
      </div>
      <div>
        <div style={{ fontSize:11, color:T.gold, letterSpacing:".12em", textTransform:"uppercase", marginBottom:10 }}>{t("salons.category")}</div>
        {[{id:"all",slug:"all",name:t("salons.categories_all")}, ...(categories||[])].map(c => (
          <label key={c.id||c.slug} style={{ display:"flex", alignItems:"center", gap:9, padding:"6px 0", cursor:"pointer" }}>
            <div onClick={() => onChange("cat", c.slug||c.id)} style={{ width:18, height:18, borderRadius:5, border:`1.5px solid ${filters.cat===(c.slug||c.id)?T.gold:T.goldBdr}`, background:filters.cat===(c.slug||c.id)?T.gold:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition: "all .2s" }}>
              {filters.cat===(c.slug||c.id) && <Check size={12} strokeWidth={3} color={T.dark} />}
            </div>
            <span style={{ fontSize:13, color:filters.cat===(c.slug||c.id)?T.text:T.muted, transition: "color .2s" }}>{catLabel(c)}</span>
          </label>
        ))}
      </div>
      <div>
        <div style={{ fontSize:11, color:T.gold, letterSpacing:".12em", textTransform:"uppercase", marginBottom:12 }}>{t("salons.price_range")} · <span style={{ color:T.goldLt }}>₹{filters.maxPrice.toLocaleString("en-IN")}</span></div>
        <input type="range" min={400} max={20000} step={200} value={filters.maxPrice} onChange={e=>onChange("maxPrice",Number(e.target.value))} style={{ width:"100%", accentColor:T.gold, cursor: "pointer" }}/>
        <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:T.subtle, marginTop:6 }}><span>₹400</span><span>₹20,000</span></div>
      </div>
      <div>
        <div style={{ fontSize:11, color:T.gold, letterSpacing:".12em", textTransform:"uppercase", marginBottom:10 }}>{t("salons.min_rating")}</div>
        {[4.5,4.0,3.5,"Any"].map(r => (
          <label key={r} style={{ display:"flex", alignItems:"center", gap:10, padding:"6px 0", cursor:"pointer" }}>
            <div onClick={() => onChange("minRating",r)} style={{ width:18, height:18, borderRadius:"50%", border:`1.5px solid ${filters.minRating===r?T.gold:T.goldBdr}`, background:filters.minRating===r?T.gold:"transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink:0, transition: "all .2s" }}>
              {filters.minRating===r && <div style={{ width: 8, height: 8, borderRadius: "50%", background: T.dark }} />}
            </div>
            <span style={{ fontSize:13, color:filters.minRating===r?T.text:T.muted, display: "flex", alignItems: "center", gap: 4 }}>
              {r === "Any" ? t("salons.any_rating") : <><Star size={12} fill={filters.minRating===r ? T.dark : "transparent"} color={filters.minRating===r ? T.dark : T.muted} /> {r}+</>}
            </span>
          </label>
        ))}
      </div>
      <div>
        <div style={{ fontSize:11, color:T.gold, letterSpacing:".12em", textTransform:"uppercase", marginBottom:10 }}>{t("salons.for_gender")}</div>
        {[["F",t("salons.gender_women")],["M",t("salons.gender_men")],["U",t("salons.gender_unisex")]].map(([v,l]) => (
          <label key={v} style={{ display:"flex", alignItems:"center", gap:10, padding:"6px 0", cursor:"pointer" }}>
            <div onClick={() => onChange("gender", filters.gender===v?null:v)} style={{ width:18, height:18, borderRadius:5, border:`1.5px solid ${filters.gender===v?T.gold:T.goldBdr}`, background:filters.gender===v?T.gold:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition: "all .2s" }}>
              {filters.gender===v && <Check size={12} strokeWidth={3} color={T.dark} />}
            </div>
            <span style={{ fontSize:13, color:filters.gender===v?T.text:T.muted }}>{l}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function SalonListing() {
  const navigate       = useNavigate();
  const [sp, setSearchParams] = useSearchParams();
  const { user }       = useAuth();
  const { t }          = useTranslation();
  const { msg, visible, show } = useToast();
  const [query,   setQuery]   = useState(sp.get("q")||"");
  const [city,    setCity]    = useState(sp.get("city")||"All Cities");
  const [sort,    setSort]    = useState("rating");
  const [view,    setView]    = useState("grid");
  const [page,    setPage]    = useState(1);
  const [filters, setFilters] = useState({ cat:sp.get("cat")||"all", maxPrice:20000, minRating:"Any", gender:null });

  const handleSearch = () => {
    const nextParams = {};
    if (query.trim()) nextParams.q = query.trim();
    if (city && city !== "All Cities") nextParams.city = city;
    if (filters.cat && filters.cat !== "all") nextParams.cat = filters.cat;
    setSearchParams(nextParams);
    setPage(1);
  };

  const handleFilter = (k,v) => { setFilters(f=>({...f,[k]:v})); setPage(1); };
  const resetFilters  = () => { setFilters({cat:"all",maxPrice:20000,minRating:"Any",gender:null}); setPage(1); };

  const params = { sort, page, per_page:12,
    ...(city && city !== "All Cities" && {city}),
    ...(filters.cat!=="all"       && {cat:filters.cat}),
    ...(filters.maxPrice<20000    && {max_price:filters.maxPrice}),
    ...(filters.minRating!=="Any" && {min_rating:filters.minRating}),
    ...(filters.gender             && {gender:filters.gender}),
    ...(query                      && {q:query}),
  };

  const { data:salonsData, isLoading, isError } = useSalons(params);
  const { data:categories }                      = useCategories();

  const salons    = salonsData?.data?.data || [];
  const meta      = salonsData?.data?.meta || {};
  const totalPages= meta.last_page || 1;

  return (
    <>
      <style>{GLOBAL_STYLES}</style>

      {/* Search hero */}
      <div style={{ background:`linear-gradient(160deg,#1A1208 0%,#0D0B08 100%)`, borderBottom:`1px solid ${T.goldBdr}`, padding:"16px 48px 24px", position:"relative", overflow:"hidden" }}>
        <Bokeh count={14}/>
        <div style={{ position:"relative", zIndex:1, maxWidth:1180, margin:"0 auto" }}>
          <div style={{ fontSize:11, color:T.gold, letterSpacing:".18em", textTransform:"uppercase", marginBottom:10 }}>{t("salons.title")}</div>
          <h1 style={{ fontFamily:"Cormorant Garamond,serif", fontSize:"clamp(28px,3.5vw,42px)", fontWeight:300, color:T.text, marginBottom:28, lineHeight:1.2 }}>
            {t("salons.subtitle")}{city && city !== "All Cities" ? " · " : ""}{city && city !== "All Cities" && <em style={{ color:T.goldLt, fontWeight:600, fontStyle:"italic" }}>{city}</em>}
          </h1>
          <div style={{ display:"flex", alignItems:"stretch", background:"rgba(26,23,16,.95)", border:`1.5px solid ${T.goldBdrS}`, borderRadius:16, overflow:"hidden", boxShadow:`0 12px 40px rgba(0,0,0,.4)`, height: 60 }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, flex:1.5, padding:"0 24px", borderRight:`1px solid ${T.goldBdr}` }}>
              <Search size={18} color={T.gold} style={{ opacity: 0.7 }} />
              <input value={query} onChange={e=>setQuery(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleSearch()} placeholder={t("salons.search_placeholder")} style={{ flex:1, background:"transparent", border:"none", outline:"none", color:T.text, fontSize:14.5, fontFamily:"inherit" }}/>
              {query && <button type="button" onClick={()=>setQuery("")} style={{ background: "none", border: "none", color:T.subtle, cursor:"pointer", display: "flex", alignItems: "center", padding: 4 }}><X size={18} /></button>}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:12, flex:1, padding:"0 24px" }}>
              <MapPin size={18} color={T.gold} style={{ opacity: 0.7 }} />
              <select value={city} onChange={e=>{setCity(e.target.value);setPage(1);}} style={{ flex:1, background:"transparent", border:"none", outline:"none", color:T.text, fontSize:14, fontFamily:"inherit", cursor:"pointer" }}>
                {CITIES.map(c=><option key={c} style={{background:T.dark3}}>{c}</option>)}
              </select>
            </div>
            <button onClick={handleSearch} style={{ padding:"0 36px", flexShrink:0, background:`linear-gradient(135deg,${T.goldLt},${T.gold})`, border:"none", color:T.dark, fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:"inherit", transition: "all .2s" }} onMouseOver={e=>e.target.style.opacity=.9} onMouseOut={e=>e.target.style.opacity=1}>{t("home.search_cta")}</button>
          </div>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"280px 1fr", gap:32, padding:"40px 48px", maxWidth:1280, margin:"0 auto" }}>
        <div style={{ position:"sticky", top:136, alignSelf:"start" }}>
          <FilterPanel filters={filters} onChange={handleFilter} onReset={resetFilters} categories={categories?.data?.data || (Array.isArray(categories?.data) ? categories.data : [])}/>
        </div>
        <div>
          {/* Toolbar */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24, flexWrap:"wrap", gap:12 }}>
            <div>
              <span style={{ fontFamily:"Cormorant Garamond,serif", fontSize:24, color:T.text }}>{isLoading?t("common.loading"):t("salons.showing",{count:meta.total||0})}</span>
              {city && city !== "All Cities" && <span style={{ fontSize:13, color:T.muted, marginLeft:10 }}>· {city}</span>}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <select value={sort} onChange={e=>{setSort(e.target.value);setPage(1);}} style={{ background:T.dark3, border:`1px solid ${T.goldBdr}`, borderRadius:10, padding:"8px 14px", color:T.text, fontSize:13, cursor:"pointer", fontFamily:"inherit", outline:"none", transition: "all .2s" }}>
                {SORT_OPTIONS.map(o=><option key={o.v} value={o.v} style={{background:T.dark3}}>{t(o.key)}</option>)}
              </select>
              <div style={{ display:"flex", background:T.dark3, border:`1px solid ${T.goldBdr}`, borderRadius:10, overflow:"hidden" }}>
                {[
                  { id: "grid", Icon: LayoutGrid },
                  { id: "list", Icon: List }
                ].map(({ id, Icon }) => (
                  <button key={id} onClick={()=>setView(id)} style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", background:view===id?T.goldDim:"transparent", border:"none", color:view===id?T.goldLt:T.muted, cursor:"pointer", transition: "all .2s" }}>
                    <Icon size={18} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {isLoading ? (
            <div style={{ display:"flex", justifyContent:"center", padding:"100px 0" }}><Spinner size={42}/></div>
          ) : isError ? (
            <div style={{ textAlign:"center", padding:"80px 40px", background:T.dark3, border:`1px solid ${T.goldBdr}`, borderRadius:16 }}>
              <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}>
                <AlertTriangle size={48} color={T.red} strokeWidth={1.5} />
              </div>
              <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:24, color:T.text, marginBottom:10 }}>{t("errors.server")}</div>
              <p style={{ fontSize:14, color:T.muted, marginBottom:24 }}>{t("errors.network")}</p>
              <BtnGold onClick={()=>window.location.reload()}>{t("common.retry")}</BtnGold>
            </div>
          ) : salons.length === 0 ? (
            <div style={{ textAlign:"center", padding:"80px 40px", background:T.dark3, border:`1px solid ${T.goldBdr}`, borderRadius:16 }}>
              <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}>
                <Search size={56} color={T.muted} strokeWidth={1} />
              </div>
              <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:24, color:T.text, marginBottom:8 }}>{t("salons.no_results_title")}</div>
              <p style={{ fontSize:14, color:T.muted, marginBottom:24 }}>{t("salons.no_results_sub")}</p>
              <BtnGold onClick={resetFilters}>{t("salons.clear_filters")}</BtnGold>
            </div>
          ) : (
            <div style={view==="grid"?{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:20}:{display:"flex",flexDirection:"column",gap:16}}>
              {salons.map(s=><SalonCard key={s.id} salon={s} view={view}/>)}
            </div>
          )}

          {totalPages > 1 && (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, marginTop:48 }}>
              <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", borderRadius:10, border:`1px solid ${T.goldBdr}`, background:"transparent", color:page===1?T.subtle:T.muted, cursor:page===1?"not-allowed":"pointer", fontFamily:"inherit", transition: "all .2s" }}>
                <ChevronLeft size={18} />
              </button>
              {Array.from({length:Math.min(totalPages,5)},(_,i)=>{ const p=Math.max(1,Math.min(page-2,totalPages-4))+i; return p<=totalPages?<button key={p} onClick={()=>setPage(p)} style={{ width:40, height:40, borderRadius:10, border:`1.5px solid ${p===page?T.gold:T.goldBdr}`, background:p===page?T.goldDim:"transparent", color:p===page?T.goldLt:T.muted, fontSize:14, fontWeight: p===page ? 700 : 400, cursor:"pointer", fontFamily:"inherit", transition: "all .2s" }}>{p}</button>:null; })}
              <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", borderRadius:10, border:`1px solid ${T.goldBdr}`, background:"transparent", color:page===totalPages?T.subtle:T.muted, cursor:page===totalPages?"not-allowed":"pointer", fontFamily:"inherit", transition: "all .2s" }}>
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>
      </div>

      <Footer/>
      <Toast msg={msg} visible={visible}/>
    </>
  );
}

